import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Scissors,
  Upload,
  RefreshCw,
  Save,
  Trash2,
  ExternalLink,
  Link2,
  CheckCircle2,
  AlertCircle,
  PlayCircle,
  Pencil,
  RotateCcw,
  Youtube,
  HardDrive,
  Mic,
  Power
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Shorts de vídeos largos» (solo admin), común a los canales principal, Gaming y English.
// Solo habla con /api/admin/clips/<canal>/*: nada de los módulos de los canales.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/clips`;
const CHUNK = 8 * 1024 * 1024;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const ACCENTS = {
  red: { btn: "bg-red-600 hover:bg-red-500 text-white", ring: "ring-red-500 border-red-500", text: "text-red-400", bar: "bg-red-500" },
  violet: { btn: "bg-violet-500 hover:bg-violet-400 text-white", ring: "ring-violet-400 border-violet-400", text: "text-violet-400", bar: "bg-violet-500" },
  cyan: { btn: "bg-cyan-500 hover:bg-cyan-400 text-slate-950", ring: "ring-cyan-400 border-cyan-400", text: "text-cyan-400", bar: "bg-cyan-500" }
};

const SRC_STATUS = {
  uploading: { label: "Subiendo", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  queued: { label: "En cola", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  analyzing: { label: "Analizando", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  waiting: { label: "Esperando a Groq", style: "bg-amber-500/15 text-amber-300 border-amber-500/30", busy: true },
  done: { label: "Analizado", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const CLIP_STATUS = {
  planned: { label: "Por montar", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  ready: { label: "Listo", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  uploading: { label: "Subiendo", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  published: { label: "Publicado", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" },
  discarded: { label: "Descartado", style: "bg-slate-800 text-slate-500 border-white/5" }
};
const CLIP_TABS = {
  pending: { label: "Por publicar", match: (c) => !["published", "discarded"].includes(c.status) },
  published: { label: "Publicados", match: (c) => c.status === "published" },
  discarded: { label: "Descartados", match: (c) => c.status === "discarded" }
};

const mmss = (s) => {
  if (s === undefined || s === null) return "—";
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};
const gb = (b) => `${(b / 1e9).toFixed(b >= 1e9 ? 1 : 2)} GB`;
const errText = (e, fallback) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const Badge = ({ map, status, spin = true }) => {
  const st = map[status] || { label: status, style: "bg-slate-800 text-slate-400 border-white/10" };
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border whitespace-nowrap ${st.style}`}>
      {st.busy && spin && <RefreshCw className="inline w-2.5 h-2.5 mr-1 animate-spin" />}
      {st.label}
    </span>
  );
};

export const LongClipsView = ({ channel, accent = "red" }) => {
  const A = ACCENTS[accent] || ACCENTS.red;
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [videos, setVideos] = useState(null);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [picked, setPicked] = useState(null);
  const [noLink, setNoLink] = useState(false);
  const [file, setFile] = useState(null);
  const [upload, setUpload] = useState(null); // {sid, sent, total}
  const [busy, setBusy] = useState(null);
  const [clipTab, setClipTab] = useState("pending");
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const resumeRef = useRef(null);
  const resumeSrc = useRef(null);

  const base = `${API}/${channel}`;

  const load = useCallback(async () => {
    try {
      const res = await axios.get(base, opts());
      setData(res.data);
      setForm((f) => f || res.data.settings);
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "No se pudo cargar la pestaña.") });
    }
  }, [base]);

  useEffect(() => {
    setData(null);
    setForm(null);
    setVideos(null);
    setPicked(null);
    load();
  }, [load]);

  // refresco mientras hay trabajo en marcha
  // apagada, solo se mueve lo que ya estaba en marcha (análisis, montaje o subida)
  const working = data && (data.sources.some((s) => (data.settings.enabled ? SRC_STATUS[s.status]?.busy && s.status !== "uploading" : s.status === "analyzing"))
    || data.clips.some((c) => (data.settings.enabled ? CLIP_STATUS[c.status]?.busy : ["rendering", "uploading"].includes(c.status))));
  useEffect(() => {
    if (!working || upload) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [working, upload, load]);

  // aviso si se cierra la página a mitad de subida
  useEffect(() => {
    if (!upload) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [upload]);

  const saveConfig = async (patch) => {
    setSaving(true);
    setFeedback(null);
    try {
      const body = patch || {
        per_day: Number(form.per_day), clips_per_video: Number(form.clips_per_video),
        min_len: Number(form.min_len), max_len: Number(form.max_len), layout: form.layout,
        captions: !!form.captions, auto_publish: !!form.auto_publish, delete_source: !!form.delete_source,
        privacy: form.privacy
      };
      const res = await axios.post(`${base}/config`, body, opts());
      setData(res.data);
      setForm(res.data.settings);
      if (!patch) setFeedback({ type: "success", text: "Configuración guardada." });
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "No se pudo guardar.") });
    } finally {
      setSaving(false);
    }
  };

  const loadVideos = async () => {
    setLoadingVideos(true);
    setFeedback(null);
    try {
      const res = await axios.get(`${base}/youtube-videos`, opts());
      setVideos(res.data.videos);
      if (!res.data.videos.length) setFeedback({ type: "error", text: "No hay vídeos de más de 3 minutos en las últimas 150 subidas del canal." });
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "No se pudieron leer los vídeos del canal.") });
    } finally {
      setLoadingVideos(false);
    }
  };

  const sendChunks = async (sid, f, start) => {
    let off = start;
    setUpload({ sid, sent: off, total: f.size });
    while (off < f.size) {
      const blob = f.slice(off, off + CHUNK);
      for (let tries = 1; ; tries++) {
        try {
          const r = await axios.put(`${base}/sources/${sid}/chunk`, blob, {
            params: { offset: off }, withCredentials: true,
            headers: { ...authHeaders(), "Content-Type": "application/octet-stream" }
          });
          off = r.data.received;
          break;
        } catch (e) {
          const rec = e.response?.data?.detail?.received;
          if (e.response?.status === 409 && rec !== undefined) { off = rec; break; }
          if (tries >= 6 || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
          await sleep(2000 * tries);
        }
      }
      setUpload({ sid, sent: off, total: f.size });
    }
    const done = await axios.post(`${base}/sources/${sid}/complete`, {}, opts());
    return done.data;
  };

  const startUpload = async () => {
    if (!file) return;
    setFeedback(null);
    try {
      const res = await axios.post(`${base}/sources`, {
        filename: file.name, size: file.size, yt_video_id: noLink ? null : picked?.id
      }, opts());
      await load();
      const done = await sendChunks(res.data.id, file, 0);
      setFeedback({
        type: done.warning ? "error" : "success",
        text: done.warning || "Vídeo subido. Ahora se transcribe y la IA elige los momentos: lo verás abajo."
      });
      setFile(null);
      setPicked(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "La subida se cortó. Pulsa «Continuar subida» en la lista y elige el mismo archivo.") });
    } finally {
      setUpload(null);
      load();
    }
  };

  const resumeUpload = async (src, f) => {
    if (!f) return;
    if (f.size !== src.size) {
      setFeedback({ type: "error", text: `Ese no es el mismo archivo (${gb(f.size)}; el que se estaba subiendo pesa ${gb(src.size)}).` });
      return;
    }
    setFeedback(null);
    try {
      const done = await sendChunks(src.id, f, src.received || 0);
      setFeedback({ type: done.warning ? "error" : "success", text: done.warning || "Subida terminada." });
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "La subida se volvió a cortar. Puedes seguir otra vez.") });
    } finally {
      setUpload(null);
      load();
    }
  };

  const act = async (key, fn, okText) => {
    setBusy(key);
    setFeedback(null);
    try {
      await fn();
      if (okText) setFeedback({ type: "success", text: okText });
    } catch (e) {
      setFeedback({ type: "error", text: errText(e, "No se pudo hacer.") });
    } finally {
      setBusy(null);
      load();
    }
  };

  if (!data || !form) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Cargando…
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = data.settings;
  const ch = data.channel;
  const lang = channel === "en" ? "inglés" : "español";
  const total = (ch.own_per_day || 0) + Number(s.per_day || 0);
  const clips = data.clips.filter(CLIP_TABS[clipTab].match);
  const counts = Object.fromEntries(Object.entries(CLIP_TABS).map(([k, t]) => [k, data.clips.filter(t.match).length]));
  const pending = data.clips.filter((c) => c.link_status === "pending");

  return (
    <div data-testid={`clips-view-${channel}`} className="space-y-6">
      {/* cabecera */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <Scissors className={`w-5 h-5 ${A.text}`} /> Shorts de tus vídeos largos · {ch.label}
            </h3>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Sube un vídeo largo que ya esté publicado en el canal. La app transcribe lo que se dice, la IA elige los
              mejores momentos y publica Shorts verticales con subtítulos, en {lang}, con el enlace al vídeo completo.
              Las partidas sin comentario se cortan por los momentos con más acción (volumen).
            </p>
          </div>
          <button
            data-testid="clips-toggle"
            onClick={() => saveConfig({ enabled: !s.enabled })}
            disabled={saving}
            className={`shrink-0 flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-black border transition ${
              s.enabled ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-slate-800 border-white/10 text-slate-400"
            }`}
          >
            <Power className="w-4 h-4" /> {s.enabled ? "ENCENDIDO" : "APAGADO"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className={`px-2 py-1 rounded-lg border ${ch.connected ? "border-emerald-500/30 text-emerald-300" : "border-red-500/30 text-red-300"}`}>
            <Youtube className="inline w-3 h-3 mr-1" />{!ch.available ? "Módulo del canal apagado" : ch.connected ? "Canal conectado" : "Canal sin conectar"}
          </span>
          <span className={`px-2 py-1 rounded-lg border ${data.groq ? "border-emerald-500/30 text-emerald-300" : "border-amber-500/30 text-amber-300"}`}>
            <Mic className="inline w-3 h-3 mr-1" />{data.groq ? "Transcripción con Groq lista" : "Sin clave de Groq: sin subtítulos"}
          </span>
          <span className="px-2 py-1 rounded-lg border border-white/10 text-slate-300">
            <HardDrive className="inline w-3 h-3 mr-1" />{data.disk_free_gb} GB libres en el servidor
          </span>
          <span className="px-2 py-1 rounded-lg border border-white/10 text-slate-300">
            Hoy: {ch.today_channel} subidas del canal + {ch.today_clips} de aquí{ch.max_daily ? ` (tope ${ch.max_daily})` : ""}
          </span>
        </div>
        {!s.enabled && (
          <p className="text-[11px] text-amber-300">Apagado: puedes subir vídeos, pero no se analizan, montan ni publican hasta que lo enciendas.</p>
        )}
        {s.enabled && s.auto_publish && data.next_publish && (
          <p className="text-[11px] text-slate-400">Próxima publicación automática: {new Date(data.next_publish).toLocaleString("es-ES")} (si hay algún Short listo).</p>
        )}
        {ch.max_daily && total > ch.max_daily && (
          <p className="text-[11px] text-amber-300">
            Ojo: el Piloto del canal ya hace unas {ch.own_per_day} subidas al día; con {s.per_day} más de aquí pasarían del tope de {ch.max_daily}.
            Cada subida gasta 1600 unidades de cuota.
          </p>
        )}
        {s.last_error && <p className="text-[11px] text-red-400 break-words">Último aviso: {s.last_error}</p>}
        {feedback && (
          <div data-testid="clips-feedback" className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${
            feedback.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}
      </div>

      {/* vídeos largos de la propia app (canal English) */}
      {channel === "en" && (
        <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h4 className="text-sm font-black text-white">Vídeos largos que hizo la app</h4>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input type="checkbox" checked={!!s.auto_from_long} onChange={() => saveConfig({ auto_from_long: !s.auto_from_long })} />
              Sacar Shorts solo de cada vídeo largo nuevo
            </label>
          </div>
          <p className="text-[11px] text-slate-500">
            Su MP4 se guarda 3 días después de publicarlo. Con la casilla marcada (y la pestaña encendida) se sacan solos.
            Si usas esto, quizá quieras poner a 0 los «Shorts-avance» de la pestaña Vídeos largos.
          </p>
          {data.app_longs.length === 0 ? (
            <p className="text-xs text-slate-500">No hay vídeos largos publicados pendientes con el archivo todavía en el servidor.</p>
          ) : data.app_longs.map((lv) => (
            <div key={lv.id} className="flex items-center justify-between gap-3 bg-black/20 border border-white/5 rounded-xl p-3">
              <div className="min-w-0">
                <p className="text-sm text-white font-bold truncate">{lv.title}</p>
                <p className="text-[11px] text-slate-500">{mmss(lv.duration)} · publicado {lv.published_at ? new Date(lv.published_at).toLocaleDateString("es-ES") : ""}</p>
              </div>
              <Button
                data-testid={`clips-from-long-${lv.id}`}
                disabled={busy === lv.id}
                onClick={() => act(lv.id, () => axios.post(`${base}/sources/from-long/${lv.id}`, {}, opts()), "Preparado: se analiza en cuanto la pestaña esté encendida.")}
                className={`${A.btn} h-8 text-xs shrink-0`}
              >
                <Scissors className="w-3.5 h-3.5 mr-1" /> Sacar Shorts
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* subir un vídeo largo */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
        <h4 className="text-sm font-black text-white">{channel === "en" ? "O sube un vídeo largo tuyo" : "Sube un vídeo largo"}</h4>
        <ol className="text-[11px] text-slate-400 list-decimal pl-4 space-y-0.5">
          <li>Elige aquí el vídeo de YouTube del que saldrán los Shorts (para poner el enlace al vídeo completo).</li>
          <li>Bájalo desde YouTube Studio → Contenido → ⋮ junto al vídeo → Descargar.</li>
          <li>Súbelo aquí. Si se corta, se puede continuar donde se quedó. Máximo {data.max_upload_gb} GB.</li>
        </ol>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="clips-load-videos" onClick={loadVideos} disabled={loadingVideos || !ch.connected} className="bg-slate-800 hover:bg-slate-700 text-white h-9 text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingVideos ? "animate-spin" : ""}`} /> {videos ? "Recargar mis vídeos largos" : "Cargar mis vídeos largos"}
            </Button>
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={noLink} onChange={() => setNoLink(!noLink)} /> Sin enlazar a un vídeo de YouTube
            </label>
          </div>
          {videos && !noLink && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
              {videos.map((v) => (
                <button
                  key={v.id}
                  data-testid={`clips-pick-${v.id}`}
                  onClick={() => setPicked(v)}
                  className={`text-left flex gap-2 p-2 rounded-xl border bg-black/20 transition min-w-0 ${picked?.id === v.id ? `ring-2 ${A.ring}` : "border-white/5 hover:border-white/20"}`}
                >
                  {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded-lg shrink-0" /> : <div className="w-24 h-14 rounded-lg bg-slate-800 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-xs text-white font-bold line-clamp-2 break-words">{v.title}</p>
                    <p className="text-[10px] text-slate-500">{mmss(v.duration)} · {v.privacy}{v.used ? " · ya usado" : ""}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {picked && !noLink && <p className="text-[11px] text-slate-300">Elegido: <b>{picked.title}</b> ({mmss(picked.duration)})</p>}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <input
            ref={fileRef}
            data-testid="clips-file"
            type="file"
            accept="video/mp4,video/quicktime,video/x-matroska,video/webm,.mp4,.mov,.mkv,.webm,.m4v"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs text-slate-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-800 file:text-white min-w-0"
          />
          <Button
            data-testid="clips-upload"
            onClick={startUpload}
            disabled={!file || !!upload || (!noLink && !picked)}
            className={`${A.btn} h-9 text-xs shrink-0`}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Subir y sacar Shorts
          </Button>
        </div>
        {!noLink && !picked && file && <p className="text-[11px] text-amber-300">Elige antes el vídeo de YouTube (o marca «Sin enlazar»).</p>}
        {upload && (
          <div className="space-y-1">
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className={`h-full ${A.bar}`} style={{ width: `${Math.round((upload.sent / upload.total) * 100)}%` }} />
            </div>
            <p className="text-[11px] text-slate-400">Subiendo… {gb(upload.sent)} de {gb(upload.total)} ({Math.round((upload.sent / upload.total) * 100)} %). No cierres esta pestaña.</p>
          </div>
        )}
        <input ref={resumeRef} type="file" className="hidden" accept="video/*,.mkv" onChange={(e) => {
          const f = e.target.files?.[0];
          if (resumeSrc.current) resumeUpload(resumeSrc.current, f);
          e.target.value = "";
        }} />
      </div>

      {/* configuración */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
        <h4 className="text-sm font-black text-white">Configuración</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="text-[11px] text-slate-400 space-y-1">
            <span>Publicar al día</span>
            <select value={form.per_day} onChange={(e) => setForm({ ...form, per_day: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-slate-400 space-y-1">
            <span>Shorts por vídeo largo</span>
            <select value={form.clips_per_video} onChange={(e) => setForm({ ...form, clips_per_video: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs">
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-slate-400 space-y-1">
            <span>Duración mínima (s)</span>
            <input type="number" min={10} max={90} value={form.min_len} onChange={(e) => setForm({ ...form, min_len: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs" />
          </label>
          <label className="text-[11px] text-slate-400 space-y-1">
            <span>Duración máxima (s)</span>
            <input type="number" min={15} max={180} value={form.max_len} onChange={(e) => setForm({ ...form, max_len: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs" />
          </label>
          <label className="text-[11px] text-slate-400 space-y-1 col-span-2">
            <span>Encuadre</span>
            <select value={form.layout} onChange={(e) => setForm({ ...form, layout: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs">
              <option value="fit">Vídeo entero con fondo difuminado</option>
              <option value="zoom">Ampliado (se ve más grande, recorta los lados)</option>
            </select>
          </label>
          {channel !== "main" && (
            <label className="text-[11px] text-slate-400 space-y-1 col-span-2">
              <span>Privacidad</span>
              <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg h-9 px-2 text-white text-xs">
                <option value="public">Público</option>
                <option value="unlisted">Oculto</option>
                <option value="private">Privado</option>
              </select>
            </label>
          )}
        </div>
        <div className="flex flex-col gap-2 text-xs text-slate-300">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.auto_publish} onChange={() => setForm({ ...form, auto_publish: !form.auto_publish })} /> Publicar solos, repartidos en el día (si no, esperan a que pulses «Publicar»)</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.captions} onChange={() => setForm({ ...form, captions: !form.captions })} /> Subtítulos palabra a palabra</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!form.delete_source} onChange={() => setForm({ ...form, delete_source: !form.delete_source })} /> Borrar el vídeo largo del servidor cuando sus Shorts estén montados (ya está en YouTube)</label>
        </div>
        {Number(form.max_len) > 60 && (
          <p className="text-[11px] text-amber-300">Ojo: un Short de más de 60 s con música o imágenes con derechos (Content ID, típico en partidas) queda bloqueado en YouTube.</p>
        )}
        <Button data-testid="clips-save" onClick={() => saveConfig()} disabled={saving} className={`${A.btn} h-9 text-xs`}>
          <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar
        </Button>
      </div>

      {/* vídeos largos subidos */}
      {data.sources.length > 0 && (
        <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
          <h4 className="text-sm font-black text-white">Vídeos largos</h4>
          {data.sources.map((src) => (
            <div key={src.id} data-testid={`clips-source-${src.id}`} className="bg-black/20 border border-white/5 rounded-xl p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-white font-bold break-words">{src.title || src.filename}</p>
                  <p className="text-[11px] text-slate-500">
                    {src.duration ? `${mmss(src.duration)} · ` : ""}{gb(src.size || 0)}
                    {src.yt_url && <> · <a href={src.yt_url} target="_blank" rel="noreferrer" className="underline hover:text-white">en YouTube</a></>}
                    {src.words_count ? ` · ${src.words_count} palabras transcritas` : ""}
                  </p>
                </div>
                <Badge map={SRC_STATUS} status={src.status} spin={src.status === "uploading" ? upload?.sid === src.id : src.status === "analyzing" || !!s.enabled} />
              </div>
              {src.progress && <p className="text-[11px] text-slate-400 break-words">{src.progress}</p>}
              {src.warning && <p className="text-[11px] text-amber-300 break-words">{src.warning}</p>}
              {(src.notes || []).map((n) => <p key={n} className="text-[11px] text-amber-300 break-words">{n}</p>)}
              {src.error && <p className="text-[11px] text-red-400 break-words">{src.error}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                {src.status === "uploading" && !upload && (
                  <Button onClick={() => { resumeSrc.current = src; resumeRef.current?.click(); }} className="bg-slate-800 hover:bg-slate-700 text-white h-7 text-[11px]">
                    <Upload className="w-3 h-3 mr-1" /> Continuar subida ({Math.round(((src.received || 0) / src.size) * 100)} %)
                  </Button>
                )}
                {["failed", "waiting"].includes(src.status) && (
                  <Button onClick={() => act(src.id, () => axios.post(`${base}/sources/${src.id}/retry`, {}, opts()))} disabled={busy === src.id} className="bg-slate-800 hover:bg-slate-700 text-white h-7 text-[11px]">
                    <RotateCcw className="w-3 h-3 mr-1" /> Reintentar ahora
                  </Button>
                )}
                <Button
                  onClick={() => window.confirm("¿Borrar este vídeo largo y sus Shorts sin publicar? Los publicados siguen en YouTube.") && act(src.id, () => axios.delete(`${base}/sources/${src.id}`, opts()))}
                  disabled={busy === src.id || src.status === "analyzing"}
                  className="bg-transparent hover:bg-red-500/10 text-red-300 h-7 text-[11px]"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Borrar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* por enlazar en Studio */}
      {pending.length > 0 && (
        <div className="bg-[#12141D] border border-amber-500/20 rounded-2xl p-6 space-y-2">
          <h4 className="text-sm font-black text-white flex items-center gap-2"><Link2 className="w-4 h-4 text-amber-400" /> Shorts por enlazar al vídeo completo</h4>
          <p className="text-[11px] text-slate-400">YouTube no deja poner el «vídeo relacionado» por la API: ábrelo en Studio, elige el vídeo largo en «Vídeo relacionado» y marca «Ya enlazado».</p>
          {pending.map((c) => (
            <div key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-black/20 rounded-xl p-2">
              <p className="text-xs text-white truncate min-w-0">{c.title}</p>
              <div className="flex gap-2 shrink-0">
                <a href={c.studio_url} target="_blank" rel="noreferrer" className="h-7 px-2 text-[11px] text-slate-200 bg-slate-800 hover:bg-slate-700 rounded-md flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Abrir en Studio</a>
                <Button onClick={() => act(c.id, () => axios.post(`${base}/clips/${c.id}/linked`, {}, opts()))} className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-[11px]">Ya enlazado</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shorts */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-black text-white">Shorts</h4>
          <div className="flex gap-1">
            {Object.entries(CLIP_TABS).map(([k, t]) => (
              <button key={k} onClick={() => setClipTab(k)} className={`px-3 h-8 rounded-lg text-xs font-bold ${clipTab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>
                {t.label} ({counts[k]})
              </button>
            ))}
          </div>
        </div>
        {clips.length === 0 && <p className="text-xs text-slate-500">Nada por aquí todavía.</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {clips.map((c) => {
            const hasFile = c.video_url && !c.file_deleted && ["ready", "uploading", "published", "discarded"].includes(c.status);
            const isEdit = editing?.id === c.id;
            return (
              <div key={c.id} data-testid={`clip-${c.id}`} className="bg-black/20 border border-white/5 rounded-xl p-3 flex gap-3 min-w-0">
                {hasFile ? (
                  <video src={`${BACKEND_URL}${c.video_url}`} controls preload="none" className="w-28 sm:w-32 aspect-[9/16] rounded-lg bg-black shrink-0" />
                ) : (
                  <div className="w-28 sm:w-32 aspect-[9/16] rounded-lg bg-slate-900 shrink-0 flex items-center justify-center"><PlayCircle className="w-6 h-6 text-slate-700" /></div>
                )}
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <Badge map={CLIP_STATUS} status={c.status} spin={["rendering", "uploading"].includes(c.status) || !!s.enabled} />
                    <span className="text-[10px] text-slate-500 font-mono">{mmss(c.start)}–{mmss(c.end)} · ★{c.score}</span>
                  </div>
                  {isEdit ? (
                    <div className="space-y-1.5">
                      <input value={editing.hook} onChange={(e) => setEditing({ ...editing, hook: e.target.value })} maxLength={60} placeholder="Gancho (texto en el vídeo)" className="w-full bg-black/40 border border-white/10 rounded-lg h-8 px-2 text-white text-xs" />
                      <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} maxLength={95} placeholder="Título de YouTube" className="w-full bg-black/40 border border-white/10 rounded-lg h-8 px-2 text-white text-xs" />
                      <div className="flex gap-2">
                        <Button onClick={() => act(c.id, async () => { await axios.post(`${base}/clips/${c.id}/edit`, { hook: editing.hook, title: editing.title }, opts()); setEditing(null); }, editing.hook !== c.hook ? "Guardado: el gancho va dibujado en el vídeo, así que se vuelve a montar." : "Guardado.")} className={`${A.btn} h-7 text-[11px]`}>Guardar</Button>
                        <Button onClick={() => setEditing(null)} className="bg-slate-800 hover:bg-slate-700 text-white h-7 text-[11px]">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-white font-black break-words">{c.hook}</p>
                      <p className="text-[11px] text-slate-300 break-words">{c.title}</p>
                    </>
                  )}
                  {c.reason && <p className="text-[11px] text-slate-500 break-words">{c.reason}</p>}
                  {c.progress && <p className="text-[11px] text-slate-400 break-words">{c.progress}</p>}
                  {c.error && c.status !== "published" && <p className="text-[11px] text-red-400 break-words">{c.error}</p>}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {c.status === "ready" && (
                      <Button data-testid={`clip-publish-${c.id}`} onClick={() => act(c.id, () => axios.post(`${base}/clips/${c.id}/publish`, {}, opts()), "Publicado en YouTube.")} disabled={busy === c.id || !ch.connected} className={`${A.btn} h-7 text-[11px]`}>
                        <Youtube className="w-3 h-3 mr-1" /> Publicar ahora
                      </Button>
                    )}
                    {["planned", "ready", "failed"].includes(c.status) && !isEdit && (
                      <Button onClick={() => setEditing({ id: c.id, hook: c.hook || "", title: c.title || "" })} className="bg-slate-800 hover:bg-slate-700 text-white h-7 text-[11px]">
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                    )}
                    {["planned", "ready", "failed"].includes(c.status) && (
                      <Button onClick={() => act(c.id, () => axios.post(`${base}/clips/${c.id}/discard`, {}, opts()))} disabled={busy === c.id} className="bg-transparent hover:bg-white/5 text-slate-400 h-7 text-[11px]">Descartar</Button>
                    )}
                    {["discarded", "failed"].includes(c.status) && (
                      <Button onClick={() => act(c.id, () => axios.post(`${base}/clips/${c.id}/restore`, {}, opts()))} disabled={busy === c.id} className="bg-slate-800 hover:bg-slate-700 text-white h-7 text-[11px]">
                        <RotateCcw className="w-3 h-3 mr-1" /> {c.status === "failed" ? "Reintentar" : "Recuperar"}
                      </Button>
                    )}
                    {c.yt_url && (
                      <a href={c.yt_url} target="_blank" rel="noreferrer" className="h-7 px-2 text-[11px] text-slate-200 bg-slate-800 hover:bg-slate-700 rounded-md flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Ver en YouTube
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LongClipsView;
